import type { ReactNode } from "react"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import {
	IconChevronRightToLineOutline18 as SkipIcon,
	IconArrowsOppositeDirectionYOutline18 as UnchangedIcon,
} from "nucleo-ui-outline-18"

import type { HunkRange } from "@productlane/lib/src/prosemirror-diff"
import { cn } from "@productlane/lib/src/browser"
import {
	decorateDocsPanels,
	handleDocsPanelClick,
} from "@productlane/lib/src/docs-tabs"
import { highlightCodeBlocksHtml } from "@productlane/lib/src/highlight-code"
import {
	computeProposalDiffStats,
	computeSideBySideRows,
} from "@productlane/lib/src/prosemirror-diff"
import { Button, NucleoFillIcon, typographyVariants } from "@productlane/ui"

import type {
	ColumnSection,
	DiffSide,
	FlatSection,
	SplitDiffEntry,
} from "./review-split-rows"
import type { WordDiffSide } from "./review-word-diff"
import { DefaultEditor } from "@/components/editor/default-editor"
import { onlyTogglesDetails } from "./review-details-toggle"
import {
	collapseUnchanged,
	isEmptyBlockHtml,
	isRuleOnlyHtml,
	toColumnSections,
	toFlatSections,
} from "./review-split-rows"
import { markChangedWords } from "./review-word-diff"
import { WordDiffHighlight } from "./review-word-diff-extension"
import { useDocsPanels } from "./use-docs-panels"

export type ReviewDiffLayout = "flat" | "split"

export function readReviewViewMode(stored: string | null): ReviewDiffLayout {
	return stored === "split" ? "split" : "flat"
}

export interface TitleChange {
	from: string | null
	to: string
}

interface Props {
	layout: ReviewDiffLayout
	icon: string
	articleTitle: string
	baseHtml: string
	mergedHtml: string
	hunkRanges?: HunkRange[] | null
	// Rendered ahead of the body when the proposal renames the article; `from`
	// is null when the article is new and has no title to replace.
	titleChange?: TitleChange | null
	hoveredProposalId?: string | null
	onHoverProposal?: (proposalId: string | null) => void
	onDiscardHunk?: (hunkId: string) => void
	// Shown when the article is too large to pair block by block.
	fallback?: ReactNode
	canEditProposed?: boolean
	onEditProposed?: (
		key: string,
		edit: { html: string; rowIndices: number[] },
	) => void
	onEditProposedTitle?: (title: string) => void
	foldUnchanged?: boolean
	// The Agent page sets this inside its own column, which already has the
	// width and padding the full review screen adds here.
	className?: string
}

export const ReviewDiff = memo(function ReviewDiff({
	layout,
	icon,
	articleTitle,
	baseHtml,
	mergedHtml,
	hunkRanges,
	titleChange,
	hoveredProposalId,
	onHoverProposal,
	onDiscardHunk,
	fallback,
	canEditProposed = false,
	onEditProposed,
	onEditProposedTitle,
	foldUnchanged = true,
	className,
}: Props) {
	const rows = useMemo(
		() => computeSideBySideRows(baseHtml, mergedHtml, hunkRanges),
		[baseHtml, mergedHtml, hunkRanges],
	)
	const [expandedGaps, setExpandedGaps] = useState<ReadonlySet<string>>(
		() => new Set(),
	)
	const entries = useMemo<SplitDiffEntry[]>(
		() =>
			foldUnchanged
				? collapseUnchanged(rows, expandedGaps)
				: rows.map((row, index) => ({ type: "row", row, index })),
		[rows, expandedGaps, foldUnchanged],
	)
	// Hovering either half of a change lights up both, so the two columns stay
	// tied together without a shared grid row.
	const [activeChangeId, setActiveChangeId] = useState<string | null>(null)
	// The same call the suggestions rail badge makes, on the same two documents,
	// so the header never contradicts the number the reviewer clicked in on.
	const stats = useMemo(() => {
		try {
			return computeProposalDiffStats(baseHtml, mergedHtml)
		} catch {
			return { added: 0, removed: 0 }
		}
	}, [baseHtml, mergedHtml])
	const bodyRef = useRef<HTMLDivElement>(null)
	// Panel switching binds as the container mounts rather than from an effect, so
	// a bar that arrived as markup is live on the first paint.
	const attachBody = useCallback((node: HTMLDivElement | null) => {
		bodyRef.current = node
		if (!node || node.dataset.panelClicks === "true") return
		node.dataset.panelClicks = "true"
		node.addEventListener("click", (event) => {
			handleDocsPanelClick(event.target as HTMLElement, node)
		})
	}, [])
	useDocsPanels(bodyRef, entries)
	if (rows.length === 0) return <>{fallback}</>

	const expandGap = (key: string) => {
		setExpandedGaps((prev) => new Set(prev).add(key))
	}
	const isSplit = layout === "split"

	const bodyProps = {
		canEditProposed,
		onEditProposed,
		onEditProposedTitle,
		entries,
		articleTitle,
		titleChange,
		hoveredProposalId,
		onHoverProposal,
		onDiscardHunk,
		onExpandGap: expandGap,
		activeChangeId,
		onActiveChange: setActiveChangeId,
	}

	return (
		<div
			ref={attachBody}
			className={cn(
				"review-typography mx-auto w-full px-4 pb-20 pt-8 sm:px-8",
				isSplit ? "max-w-[1240px]" : "max-w-[720px]",
				className,
			)}
		>
			<Heading icon={icon} title={articleTitle} stats={stats} />
			{isSplit ? <SplitBody {...bodyProps} /> : <FlatBody {...bodyProps} />}
		</div>
	)
})

function Heading({
	icon,
	title,
	stats,
}: {
	icon: string
	title: string
	stats: { added: number; removed: number }
}) {
	const unchanged = stats.added === 0 && stats.removed === 0
	return (
		<div className="mb-6 flex items-center gap-2">
			<NucleoFillIcon
				icon={icon}
				className="h-4 w-4 flex-none text-gray-600 dark:text-gray-300"
			/>
			<h1 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
				{title}
			</h1>
			{unchanged ? (
				<span className="flex-none text-xs font-medium text-gray-500 dark:text-gray-400">
					No content changes
				</span>
			) : (
				<span className="flex flex-none items-center gap-1.5 text-xs font-medium tabular-nums">
					<span className="text-green-600 dark:text-green-400">
						+{stats.added}
					</span>
					<span className="text-red-600 dark:text-red-400">
						&minus;{stats.removed}
					</span>
					<span className="text-gray-400 dark:text-gray-500">lines</span>
				</span>
			)}
		</div>
	)
}

interface BodyProps {
	onEditProposedTitle?: (title: string) => void
	canEditProposed?: boolean
	onEditProposed?: (
		key: string,
		edit: { html: string; rowIndices: number[] },
	) => void
	entries: SplitDiffEntry[]
	articleTitle: string
	titleChange?: TitleChange | null
	hoveredProposalId?: string | null
	onHoverProposal?: (proposalId: string | null) => void
	onDiscardHunk?: (hunkId: string) => void
	onExpandGap: (key: string) => void
	activeChangeId: string | null
	onActiveChange: (changeId: string | null) => void
}

// A suggestion that leaves the title alone still lets the reviewer rename it.
function showsUnchangedTitle({
	titleChange,
	canEditProposed,
	onEditProposedTitle,
}: Pick<
	BodyProps,
	"titleChange" | "canEditProposed" | "onEditProposedTitle"
>): boolean {
	return !titleChange && !!canEditProposed && !!onEditProposedTitle
}

function FlatBody({
	canEditProposed,
	onEditProposed,
	onEditProposedTitle,
	entries,
	articleTitle,
	titleChange,
	hoveredProposalId,
	onHoverProposal,
	onDiscardHunk,
	onExpandGap,
}: BodyProps) {
	const sections = useMemo(() => toFlatSections(entries), [entries])

	return (
		<div>
			{showsUnchangedTitle({
				titleChange,
				canEditProposed,
				onEditProposedTitle,
			}) && (
				<UnchangedTitle
					value={articleTitle}
					editable
					onEdit={onEditProposedTitle}
				/>
			)}
			{titleChange && (
				<div className="mb-6" data-diff-row="title">
					<TitleLabel />
					{titleChange.from !== null && (
						<Passage tone="removed" isStruck>
							<TitleValue value={titleChange.from} />
						</Passage>
					)}
					<Passage tone="added">
						<TitleValue
							value={titleChange.to}
							editable={canEditProposed}
							onEdit={onEditProposedTitle}
						/>
					</Passage>
				</div>
			)}
			{sections.map((section) => {
				if (section.kind === "gap") {
					return (
						<GapLink
							key={section.key}
							count={section.count}
							onExpand={() => onExpandGap(section.key)}
						/>
					)
				}
				if (section.kind === "prose") {
					return (
						<Passage
							key={section.key}
							html={section.html}
							editable={canEditProposed}
							onEdit={(html) =>
								onEditProposed?.(section.key, {
									html,
									rowIndices: section.rowIndices,
								})
							}
						/>
					)
				}

				return (
					<ChangeSection
						key={section.key}
						section={section}
						canEdit={canEditProposed}
						onEdit={(html) =>
							onEditProposed?.(section.key, {
								html,
								rowIndices: section.rowIndices,
							})
						}
						hoveredProposalId={hoveredProposalId}
						onHoverProposal={onHoverProposal}
						onDiscardHunk={onDiscardHunk}
					/>
				)
			})}
		</div>
	)
}

function ChangeSection({
	section,
	canEdit = false,
	onEdit,
	hoveredProposalId,
	onHoverProposal,
	onDiscardHunk,
}: {
	section: Extract<FlatSection, { kind: "change" }>
	canEdit?: boolean
	onEdit?: (html: string) => void
	hoveredProposalId?: string | null
	onHoverProposal?: (proposalId: string | null) => void
	onDiscardHunk?: (hunkId: string) => void
}) {
	const { row } = section
	const marks = row.kind === "replace"
	const removedHtml = row.kind === "insert" ? null : (row.left?.html ?? null)
	const addedHtml = row.kind === "delete" ? null : (row.right?.html ?? null)

	const proposalId = row.proposalIds[0] ?? null
	const hunkId = row.hunkIds[0] ?? null

	return (
		<div
			data-proposal-id={proposalId ?? undefined}
			data-diff-row={row.kind}
			className={cn(
				"group/row relative",
				hoveredProposalId != null &&
					row.proposalIds.includes(hoveredProposalId) &&
					"rounded-md ring-1 ring-gray-300 dark:ring-gray-600",
			)}
			onMouseEnter={() => onHoverProposal?.(proposalId)}
			onMouseLeave={() => onHoverProposal?.(null)}
		>
			{removedHtml !== null && (
				<Passage
					tone="removed"
					html={removedHtml}
					wordDiff={
						marks
							? { otherText: row.right?.text ?? "", side: "delete" }
							: undefined
					}
					isStruck
				/>
			)}
			{addedHtml !== null && (
				<Passage
					tone="added"
					html={addedHtml}
					wordDiff={
						marks
							? { otherText: row.left?.text ?? "", side: "insert" }
							: undefined
					}
					editable={canEdit}
					onEdit={onEdit}
				/>
			)}
			{hunkId && onDiscardHunk && (
				<div className="pointer-events-none absolute -top-2 right-0 opacity-0 group-hover/row:pointer-events-auto group-hover/row:opacity-100">
					<Button
						variant="glass"
						size="xs"
						Icon={SkipIcon}
						onClick={() => onDiscardHunk(hunkId)}
					>
						Skip
					</Button>
				</div>
			)}
		</div>
	)
}

const editableFieldClass = "cursor-text outline-none"

const wordDiffClass = cn(
	"[&_[data-diff-word='delete']]:rounded-sm [&_[data-diff-word='delete']]:bg-red-500/20 [&_[data-diff-word='delete']]:px-0.5",
	"[&_[data-diff-word='insert']]:rounded-sm [&_[data-diff-word='insert']]:bg-green-500/25 [&_[data-diff-word='insert']]:px-0.5",
)

type Tone = "removed" | "added" | "neutral"

interface WordDiff {
	otherText: string
	side: WordDiffSide
}

// A run of article content. Unchanged content is bare prose; changed content
// carries a tint and a colour bar out in the left margin, so the change reads
// as an annotation on the article rather than a cell in a table.
function Passage({
	html,
	tone = "neutral",
	children,
	isStruck = false,
	isActive = false,
	editable = false,
	onEdit,
	wordDiff,
}: {
	html?: string
	tone?: Tone
	children?: ReactNode
	isStruck?: boolean
	isActive?: boolean
	editable?: boolean
	onEdit?: (html: string) => void
	wordDiff?: WordDiff
}) {
	const seededHtml = useRef(html).current
	const [isWiped, setIsWiped] = useState(
		() => editable && isEmptyBlockHtml(html ?? ""),
	)
	const otherText = wordDiff?.otherText
	const side = wordDiff?.side
	// The editor strips unknown spans on parse, so the editable side highlights
	// through decorations while the static side gets the spans baked into html.
	const markedHtml = useMemo(() => {
		if (html === undefined || editable) return html
		const marked =
			otherText === undefined || !side
				? html
				: markChangedWords(html, otherText, side)
		// The tab bar and the highlighting ship as markup rather than waiting on an
		// effect, so a block with tabs or a code group never renders as its first
		// panel alone and code never renders unstyled.
		return highlightCodeBlocksHtml(decorateDocsPanels(marked))
	}, [html, editable, otherText, side])
	const editorExtensions = useMemo(
		() =>
			otherText !== undefined && side
				? [WordDiffHighlight.configure({ otherText, side })]
				: [],
		[otherText, side],
	)
	// A block the reviewer emptied keeps its caret line but loses the tint, so
	// an empty tinted band never reads as a change that is still there.
	const isRule = html !== undefined && isRuleOnlyHtml(html)
	const tinted = tone !== "neutral" && !isWiped && !isRule
	const markedRule = tone !== "neutral" && isRule
	return (
		<div
			data-tinted={tinted || undefined}
			className={cn(
				"relative",
				// The tint bleeds into the margin so changed text stays on the same
				// left edge as the unchanged text around it.
				tinted && "-mx-2 my-2.5 rounded-md px-2 py-1.5",
				tinted &&
					"before:absolute before:-left-1.5 before:bottom-1 before:top-1",
				tinted && "before:w-0.5 before:rounded-full before:content-['']",
				tinted &&
					tone === "removed" &&
					"bg-red-500/[0.06] before:bg-red-500/50 dark:bg-red-500/[0.10]",
				tinted &&
					tone === "added" &&
					"bg-green-500/[0.07] before:bg-green-500/50 dark:bg-green-500/[0.11]",
				// Hovering either half lights up both, so a rewrite reads as one
				// change rather than two unrelated tinted passages.
				tinted &&
					isActive &&
					tone === "removed" &&
					"bg-red-500/[0.13] before:bg-red-500 dark:bg-red-500/[0.18]",
				tinted &&
					isActive &&
					tone === "added" &&
					"bg-green-500/[0.15] before:bg-green-500 dark:bg-green-500/[0.20]",
				// A divider is one pixel, so its change mark is the rule's own colour
				// plus a sign in the margin, never a band around it.
				markedRule && "my-6 [&_hr]:!my-0 [&_hr]:!border-t-[1px]",
				markedRule &&
					"before:absolute before:-left-[19px] before:-top-[9px] before:w-3 before:text-center before:text-xs before:font-medium before:leading-[18px]",
				markedRule &&
					tone === "added" &&
					"text-green-600 before:content-['+'] dark:text-green-400 [&_hr]:!border-green-500/60",
				markedRule &&
					tone === "removed" &&
					"text-red-600 before:content-['−'] dark:text-red-400 [&_hr]:!border-red-500/60",
				// Struck wording is the clearest "this is on its way out" signal when
				// the old and new sit stacked; side by side the tint already says it.
				isStruck &&
					!markedRule &&
					tone === "removed" &&
					"text-gray-500 line-through decoration-red-500/40 dark:text-gray-400",
			)}
		>
			{editable ? (
				<DefaultEditor
					initialContent={seededHtml}
					extensions={editorExtensions}
					spellCheck={false}
					// A passage is one slice of the article, so a heading or code block
					// ending it would gain an empty paragraph that ships on accept.
					trailingNode={false}
					onUpdate={({ editor, transaction }) => {
						if (onlyTogglesDetails(transaction)) return
						const next = editor.getHTML()
						setIsWiped(isEmptyBlockHtml(next))
						onEdit?.(next)
					}}
					className={cn(
						"max-w-none break-words",
						typographyVariants({ variant: "editor", mode: "invert" }),
						"[&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0",
						wordDiffClass,
						editableFieldClass,
					)}
				/>
			) : (
				<div
					className={cn(
						"max-w-none break-words",
						typographyVariants({ variant: "editor", mode: "invert" }),
						// The passage owns the spacing between its own blocks; the first
						// and last must not push the tint away from the text.
						"[&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0",
						wordDiffClass,
					)}
					{...(markedHtml !== undefined
						? { dangerouslySetInnerHTML: { __html: markedHtml } }
						: { children })}
				/>
			)}
		</div>
	)
}

function GapLink({ count, onExpand }: { count: number; onExpand: () => void }) {
	return (
		<div className="my-4 flex items-center gap-3">
			<span className="h-px flex-1 bg-gray-200 dark:bg-gray-700/80" />
			<button
				type="button"
				onClick={onExpand}
				className="flex flex-none items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
			>
				<UnchangedIcon aria-hidden className="h-3.5 w-3.5 flex-none" />
				{count === 1 ? "1 unchanged block" : `${count} unchanged blocks`}
			</button>
			<span className="h-px flex-1 bg-gray-200 dark:bg-gray-700/80" />
		</div>
	)
}

function SplitBody({
	canEditProposed,
	onEditProposed,
	onEditProposedTitle,
	entries,
	articleTitle,
	titleChange,
	hoveredProposalId,
	onHoverProposal,
	onDiscardHunk,
	onExpandGap,
	activeChangeId,
	onActiveChange,
}: BodyProps) {
	const left = useMemo(() => toColumnSections(entries, "left"), [entries])
	const right = useMemo(() => toColumnSections(entries, "right"), [entries])
	const shared = {
		hoveredProposalId,
		onHoverProposal,
		onExpandGap,
		activeChangeId,
		onActiveChange,
	}
	const unchangedTitle = showsUnchangedTitle({
		titleChange,
		canEditProposed,
		onEditProposedTitle,
	})

	return (
		// Each column flows on its own. Pairing lives in the shared change id, not
		// in a shared grid row, so a run of removals no longer punches a hole in
		// the opposite column.
		<div className="grid grid-cols-2 items-start gap-x-10">
			<div>
				<ColumnLabel>Current</ColumnLabel>
				{unchangedTitle && <UnchangedTitle value={articleTitle} />}
				{titleChange?.from != null && (
					<Passage tone="removed" isStruck>
						<TitleLabel />
						<TitleValue value={titleChange.from} />
					</Passage>
				)}
				<Column sections={left} side="left" {...shared} />
			</div>
			<div>
				<ColumnLabel>Proposed</ColumnLabel>
				{unchangedTitle && (
					<UnchangedTitle
						value={articleTitle}
						editable
						onEdit={onEditProposedTitle}
					/>
				)}
				{titleChange && (
					<Passage tone="added">
						<TitleLabel />
						<TitleValue
							value={titleChange.to}
							editable={canEditProposed}
							onEdit={onEditProposedTitle}
						/>
					</Passage>
				)}
				<Column
					canEdit={canEditProposed}
					onEdit={onEditProposed}
					sections={right}
					side="right"
					onDiscardHunk={onDiscardHunk}
					{...shared}
				/>
			</div>
		</div>
	)
}

function Column({
	sections,
	side,
	canEdit = false,
	onEdit,
	hoveredProposalId,
	onHoverProposal,
	onDiscardHunk,
	onExpandGap,
	activeChangeId,
	onActiveChange,
}: {
	sections: ColumnSection[]
	side: DiffSide
	canEdit?: boolean
	onEdit?: (key: string, edit: { html: string; rowIndices: number[] }) => void
	hoveredProposalId?: string | null
	onHoverProposal?: (proposalId: string | null) => void
	onDiscardHunk?: (hunkId: string) => void
	onExpandGap: (key: string) => void
	activeChangeId: string | null
	onActiveChange: (changeId: string | null) => void
}) {
	return (
		<div data-column={side}>
			{sections.map((section) => {
				if (section.kind === "gap") {
					return (
						<GapLink
							key={section.key}
							count={section.count}
							onExpand={() => onExpandGap(section.key)}
						/>
					)
				}
				if (section.kind === "prose") {
					return (
						<Passage
							key={section.key}
							html={section.html}
							editable={canEdit}
							onEdit={(html) =>
								onEdit?.(section.key, {
									html,
									rowIndices: section.rowIndices,
								})
							}
						/>
					)
				}
				return (
					<ColumnChange
						key={section.key}
						section={section}
						side={side}
						canEdit={canEdit}
						onEdit={(html) =>
							onEdit?.(section.key, { html, rowIndices: section.rowIndices })
						}
						isActive={activeChangeId === section.changeId}
						isProposalActive={
							hoveredProposalId != null &&
							section.row.proposalIds.includes(hoveredProposalId)
						}
						onHoverProposal={onHoverProposal}
						onDiscardHunk={onDiscardHunk}
						onActiveChange={onActiveChange}
					/>
				)
			})}
		</div>
	)
}

function ColumnChange({
	section,
	side,
	canEdit = false,
	onEdit,
	isActive,
	isProposalActive,
	onHoverProposal,
	onDiscardHunk,
	onActiveChange,
}: {
	section: Extract<ColumnSection, { kind: "change" }>
	side: DiffSide
	canEdit?: boolean
	onEdit?: (html: string) => void
	isActive: boolean
	isProposalActive: boolean
	onHoverProposal?: (proposalId: string | null) => void
	onDiscardHunk?: (hunkId: string) => void
	onActiveChange: (changeId: string | null) => void
}) {
	const { row, changeId } = section
	const other = side === "left" ? row.right?.text : row.left?.text
	const wordDiff: WordDiff | undefined =
		row.kind === "replace"
			? { otherText: other ?? "", side: side === "left" ? "delete" : "insert" }
			: undefined

	const proposalId = row.proposalIds[0] ?? null
	const hunkId = row.hunkIds[0] ?? null

	return (
		<div
			className="group/change relative"
			data-change-id={changeId}
			data-side={side}
			data-diff-row={row.kind}
			data-proposal-id={proposalId ?? undefined}
			onMouseEnter={() => {
				onActiveChange(changeId)
				onHoverProposal?.(proposalId)
			}}
			onMouseLeave={() => {
				onActiveChange(null)
				onHoverProposal?.(null)
			}}
		>
			<Passage
				html={section.html}
				wordDiff={wordDiff}
				tone={side === "left" ? "removed" : "added"}
				isActive={isActive || isProposalActive}
				editable={canEdit}
				onEdit={onEdit}
			/>
			{side === "right" && hunkId && onDiscardHunk && (
				<div className="pointer-events-none absolute -top-1 right-0 opacity-0 group-hover/change:pointer-events-auto group-hover/change:opacity-100">
					<Button
						variant="glass"
						size="xs"
						Icon={SkipIcon}
						onClick={() => onDiscardHunk(hunkId)}
					>
						Skip
					</Button>
				</div>
			)}
		</div>
	)
}

function TitleLabel() {
	return (
		<span className="mb-0.5 block text-xxs font-semibold tracking-wider text-gray-500 dark:text-gray-400">
			Title
		</span>
	)
}

function UnchangedTitle({
	value,
	editable = false,
	onEdit,
}: {
	value: string
	editable?: boolean
	onEdit?: (title: string) => void
}) {
	return (
		<div className="mb-4">
			<TitleLabel />
			<TitleValue value={value} editable={editable} onEdit={onEdit} />
		</div>
	)
}

function TitleValue({
	value,
	editable = false,
	onEdit,
}: {
	value: string
	editable?: boolean
	onEdit?: (title: string) => void
}) {
	return (
		<span
			data-diff-title={editable ? "editable" : "static"}
			className={cn(
				"block text-[15px] font-semibold",
				editable && editableFieldClass,
			)}
			role={editable ? "textbox" : undefined}
			aria-label={editable ? "Title" : undefined}
			tabIndex={editable ? 0 : undefined}
			contentEditable={editable || undefined}
			spellCheck={editable ? false : undefined}
			suppressContentEditableWarning={editable}
			onBlur={
				editable && onEdit
					? (event) => onEdit(event.currentTarget.textContent ?? "")
					: undefined
			}
			onKeyDown={
				editable
					? (event) => {
							if (event.key !== "Enter") return
							event.preventDefault()
							event.currentTarget.blur()
						}
					: undefined
			}
		>
			{value}
		</span>
	)
}

function ColumnLabel({ children }: { children: ReactNode }) {
	return (
		<div className="mb-1 text-xxs font-semibold tracking-wider text-gray-400 dark:text-gray-500">
			{children}
		</div>
	)
}
